import Highlight from '@tiptap/extension-highlight';
import { InputRule, PasteRule } from '@tiptap/core';
import { placeholderTokenPattern } from '@helpers/text-editor';

export const buildPlaceholderRegex = (tokens, { global = false, anchorEnd = false } = {}) => {
    if (!tokens || tokens.length === 0) return null;
    const parts = tokens.map(placeholderTokenPattern);
    const tail = anchorEnd ? '$' : '';
    const flags = global ? 'g' : '';
    return new RegExp(`\\[(${parts.join('|')})\\]${tail}`, flags);
};

// Must mutate state.tr in place (not chain().run()) — the input/paste rules plugin
// dispatches state.tr once the handler returns. Returning null would cancel the rule.
export const buildPlaceholderHandler =
    ({ getLabel }) =>
    ({ state, range, match }) => {
        const inner = match[1];
        if (!inner) return;
        const colonIdx = inner.indexOf(':');
        const token = colonIdx === -1 ? inner : inner.slice(0, colonIdx);
        const refId = colonIdx === -1 ? null : inner.slice(colonIdx + 1);
        const markType = state.schema.marks.highlight;
        if (!markType) return;
        state.tr.replaceWith(
            range.from,
            range.to,
            state.schema.text(getLabel(token), [markType.create({ placeholder: token, refId })]),
        );
    };

export const CustomHighlight = Highlight.extend({
    inclusive: false,
    addOptions() {
        return {
            HTMLAttributes: {},
            tokens: [],
            getLabel: token => token,
        };
    },
    addAttributes() {
        return {
            placeholder: {
                default: null,
                renderHTML: attributes =>
                    attributes.placeholder ? { 'data-placeholder': attributes.placeholder } : {},
                parseHTML: element => element.getAttribute('data-placeholder'),
            },
            refId: {
                default: null,
                renderHTML: attributes => (attributes.refId ? { 'data-ref-id': attributes.refId } : {}),
                parseHTML: element => element.getAttribute('data-ref-id'),
            },
        };
    },
    addInputRules() {
        const find = buildPlaceholderRegex(this.options.tokens, { anchorEnd: true });
        if (!find) return [];
        return [
            new InputRule({
                find,
                handler: buildPlaceholderHandler({ getLabel: this.options.getLabel }),
            }),
        ];
    },
    addPasteRules() {
        const find = buildPlaceholderRegex(this.options.tokens, { global: true });
        if (!find) return [];
        return [
            new PasteRule({
                find,
                handler: buildPlaceholderHandler({ getLabel: this.options.getLabel }),
            }),
        ];
    },
    addKeyboardShortcuts() {
        return {
            Backspace: () =>
                this.editor.commands.command(({ tr, state }) => {
                    let isHighlight = false;
                    const { selection } = state;
                    const { empty, anchor } = selection;

                    if (!empty) {
                        return false;
                    }
                    state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
                        const mark = node.marks[0];
                        if (mark && mark.type.name === this.name) {
                            isHighlight = true;
                            tr.insertText('', pos, pos + node.nodeSize);

                            return false;
                        }
                    });

                    return isHighlight;
                }),
        };
    },
});
